<template>
  <div class="customers-wrapper">
    <header class="page-header fade-in">
      <div class="header-copy">
        <h1>Clients</h1>
        <p>
          Visualisez et gérez rapidement votre portefeuille clients sans changer
          d'écran.
        </p>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-outline"
          @click="refreshCustomers"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button class="btn btn-primary" @click="goToCreate">
          <i class="bi bi-person-plus"></i>
          <span>Nouveau client</span>
        </button>
      </div>
    </header>

    <section>
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (nom, téléphone, ville...)"
          />
        </div>
        <div class="stats" v-if="filteredCustomers.length">
          <span>{{ filteredCustomers.length }} client(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des clients…</p>
      </div>

      <div v-else-if="filteredCustomers.length === 0" class="state">
        <i class="bi bi-people state-icon"></i>
        <h3>Aucun client trouvé</h3>
        <p>Essayez une autre recherche ou ajoutez un nouveau client.</p>
        <button class="btn btn-primary" @click="goToCreate">
          <i class="bi bi-person-plus"></i>
          <span>Créer un client</span>
        </button>
      </div>

      <div v-else class="customer-cards">
        <article
          v-for="customer in filteredCustomers"
          :key="customer.reference || customer.id || customer.fullName"
          class="customer-card fade-in"
          @click="viewCustomer(customer.reference)"
        >
          <div class="card-content">
            <h3 class="customer-name">
              {{ customer.fullName || customer.name }}
            </h3>
            <p v-if="customer.society" class="customer-society">
              {{ customer.society }}
            </p>
            <div class="customer-info">
              <div v-if="customer.address || customer.city" class="info-item">
                <i class="bi bi-geo-alt"></i>
                <span>{{ customer.address || customer.city }}</span>
              </div>
              <div v-if="customer.phone" class="info-item">
                <i class="bi bi-telephone"></i>
                <span>{{ customer.phone }}</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <button class="fab" @click="goToCreate">
      <i class="bi bi-plus-lg"></i>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CustomersList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("customer", { customers: "getAll" }),
    filteredCustomers() {
      if (!this.searchTerm) return this.customers || [];
      const term = this.searchTerm.toLowerCase();
      return (this.customers || []).filter((customer) =>
        [
          customer.fullName,
          customer.name,
          customer.phone,
          customer.email,
          customer.city,
          customer.reference,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase())
          .some((value) => value.includes(term))
      );
    },
  },
  methods: {
    async fetchCustomers() {
      this.loading = true;
      try {
        await this.$store.dispatch("customer/getAll");
      } catch (error) {
        console.error("Error loading customers", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de charger les clients.",
        });
      } finally {
        this.loading = false;
      }
    },
    refreshCustomers() {
      this.fetchCustomers();
    },
    goToCreate() {
      this.$router.push({ name: "customers-create" });
    },
    viewCustomer(reference) {
      this.$router.push({ name: "customers-show", params: { reference } });
    },
  },
  mounted() {
    if (!this.customers || this.customers.length === 0) {
      this.fetchCustomers();
    }
  },
};
</script>

<style scoped>
.customers-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem 2.5rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.page-header p {
  margin: 0;
  max-width: 520px;
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  min-width: 300px;
  flex: 1;
  transition: all 0.2s ease;
}

.search:focus-within {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search i {
  color: #94a3b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.95rem;
  width: 100%;
  color: #1e293b;
}

.search input::placeholder {
  color: #94a3b8;
}

.stats {
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 8px;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #64748b;
  text-align: center;
}

.state-icon {
  font-size: 4rem;
  color: #cbd5e1;
  opacity: 0.6;
}

.state h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #475569;
  font-weight: 600;
}

.state p {
  margin: 0;
  color: #94a3b8;
  font-size: 0.95rem;
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.customer-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.customer-card {
  background: white;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.customer-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.customer-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.customer-card:hover::before {
  transform: scaleX(1);
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.customer-name {
  margin: 0;
  font-size: 1.15rem;
  color: #0f172a;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.customer-society {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 500;
}

.customer-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.5;
}

.info-item i {
  color: #3b82f6;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.info-item span {
  word-break: break-word;
  flex: 1;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn i {
  font-size: 1rem;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.btn-outline {
  border: 2px solid #e2e8f0;
  background: white;
  color: #475569;
}

.btn-outline:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #f8fafc;
}

.fab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px rgba(59, 130, 246, 0.3), 0 4px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
}

.fab:hover {
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.4),
    0 6px 12px rgba(0, 0, 0, 0.15);
}

.fab:active {
  transform: scale(0.95);
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1024px) {
  .customers-wrapper {
    padding: 1.5rem 1.5rem 5rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .customer-cards {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .customers-wrapper {
    padding: 1.25rem 1rem 5rem;
  }
  .page-header h1 {
    font-size: 2rem;
  }
  .search {
    min-width: unset;
  }
  .customer-cards {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
  .fab {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 52px;
    height: 52px;
    font-size: 1.4rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .customer-card {
    padding: 1rem;
  }
  .customer-name {
    font-size: 1.05rem;
  }
  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
